import { z } from 'zod';
import { DiscountType, QuoteAftermarketProductType, QuoteFeeType } from '@trnsact/trnsact-shared-types';
import { CustomerVisibility } from 'modules/dealSheet/enums';

export const priceSchema = z.object({
  markup: z.number(),
  markupType: z.nativeEnum(DiscountType),

  discount: z.number(),
  discountType: z.nativeEnum(DiscountType),

  salesTax: z.number(),
  salesTaxType: z.nativeEnum(DiscountType),

  total: z.number(),
  netPrice: z.number(),
  retailPrice: z.number(),
  wholesalePrice: z.number(),
});

export const quoteFeeSchema = z.object({
  id: z.string().optional(),
  price: priceSchema,
  amountType: z.string(),
  description: z.string(),
  type: z.nativeEnum(QuoteFeeType),
  equipmentId: z.string().nullable(),
  isFromCommonRules: z.boolean().optional(),
  parentId: z.string().nullable().optional(),
  isTaxable: z.boolean().nullable().optional(),
  amount: z.number({ message: 'Amount Required' }),
  parentType: z.enum(['charge', 'equipment']).optional(),
  customerVisibility: z.nativeEnum(CustomerVisibility).nullable().optional(),
});

const cashBackAmountsSchema = z.object({
  amount: z.number(),
  description: z.string().min(1, { message: 'Description Required' }),
  equipmentId: z.string().nullable(),
});

export const equipmentTradeInsSchema = z.object({
  vin: z.string(),
  externalId: z.string(),
  stockNumber: z.string(),
  equipmentId: z.string().min(1),
  tradeInDetails: z.object({
    tradeAmountOwed: z.number(),
    tradeValue: z.number(),
    tradeOtherCharges: z.number().nullable(),
  }),
});

export const quoteRebateSchema = z.object({
  amount: z.number({ message: 'Amount Required' }),
  description: z.string().min(1, { message: 'Description Required' }),
  equipmentId: z.string().nullable(),
});

const quoteAttributeSchema = z.object({
  description: z.string().min(1, { message: 'Description Required' }),
  key: z.string().min(1),
  value: z.string(),
});

export const quoteEquipment = z.object({
  vin: z.string(),
  cost: z.number(),
  externalId: z.string(),
  stockNumber: z.string(),
  price: priceSchema,
  equipmentId: z.string().min(1),
  attributes: z.array(quoteAttributeSchema),
});

export const aftermarketProductsSchema = z.object({
  cost: z.number({ message: 'Cost Required' }),
  description: z.string(),
  equipmentId: z.string().nullable(),
  type: z.nativeEnum(QuoteAftermarketProductType),
});

export const createOrUpdateQuoteDTOSchema = z.object({
  term: z.number().nullable(),
  downPayment: z.number().nullable(),
  interestRate: z.number().nullable(),
  paymentAmount: z.number().nullable(),
  contractStartDate: z.string().nullable(),
  fees: z.array(quoteFeeSchema),
  firstPaymentDueDate: z.string().nullable(),
  equipment: z.array(quoteEquipment),
  rebates: z.array(quoteRebateSchema),
  attributes: z.array(quoteAttributeSchema),
  cashBackAmounts: z.array(cashBackAmountsSchema),
  vendorOpportunityId: z.string().uuid(),
  equipmentTradeIns: z.array(equipmentTradeInsSchema),
  aftermarketProducts: z.array(aftermarketProductsSchema),
});

export const formQuoteSchema = z.object({
  term: z.number().nullable(),
  downPayment: z.number().nullable(),
  interestRate: z.number().nullable(),
  paymentAmount: z.number().nullable(),
  contractStartDate: z.string().nullable(),
  fees: z.array(quoteFeeSchema),
  firstPaymentDueDate: z.string().nullable(),
  equipment: z.array(quoteEquipment),
  rebates: z.array(quoteRebateSchema),
  attributes: z.array(quoteAttributeSchema),
  cashBackAmounts: z.array(cashBackAmountsSchema),
  equipmentTradeIns: z.array(equipmentTradeInsSchema),
  aftermarketProducts: z.array(aftermarketProductsSchema),
});

export const quoteSchema = z.object({
  active: z.boolean(),
  aftermarketProducts: z.array(aftermarketProductsSchema),
  aftermarketProductsTotal: z.number(),
  attributes: z.array(quoteAttributeSchema),
  cashBackAmounts: z.array(cashBackAmountsSchema),
  cashBackAmountsTotal: z.number(),
  contractStartDate: z.string().datetime(),
  createdDateTime: z.string().datetime(),
  daysToFirstPayment: z.number(),
  downPayment: z.number(),
  equipment: z.array(quoteEquipment),
  equipmentSubtotal: z.number(),
  equipmentTradeIns: z.array(equipmentTradeInsSchema),
  externalCrmVendorOpportunityId: z.string().nullable(),
  fees: z.array(quoteFeeSchema),
  feesTotal: z.number(),
  financedAmount: z.number(),
  firstPaymentDueDate: z.string().datetime(),
  interestRate: z.number(),
  modifiedDateTime: z.string().datetime(),
  paymentAmount: z.number(),
  quoteId: z.string().uuid(),
  rebates: z.array(quoteRebateSchema),
  rebatesTotal: z.number(),
  term: z.number().nullable(),
  tradeValueGross: z.number(),
  tradeValueNet: z.number(),
  tradeValueOwed: z.number(),
  vendorOpportunityId: z.string().uuid(),
});
